import { defineStore } from 'pinia'
import { api } from '@/utils/api'
import { chatSocket } from '@/utils/chatSocket'
import { useUserStore } from '@/store/UserStore'
import { playSound } from '@/utils/sound'
import router from '@/router'

export const useChatStore = defineStore('chat', {
    state: () => ({
        chats: [],
        activeChat: null,
        messages: [],
        members: [],
        hasMoreMessages: true,
        oldestMessageId: null,       // для cursor-пагинации вверх
        newestMessageId: null,       // для cursor-пагинации вниз (контекстный режим)
        hasMoreMessagesAfter: false, // есть ли сообщения ниже (контекстный режим)
        isLoadingAfter: false,
        isContextMode: false,        // просматриваем контекст, не live
        highlightedMessageId: null,  // ID сообщения для подсветки после навигации
        searchResults: [],
        isLoadingChats: false,
        isLoadingMessages: false,
        isLoadingMore: false,
        isSending: false,
        isSearching: false,
        currentSubscription: null,
    }),

    getters: {
        sortedChats: (state) => [...state.chats].sort((a, b) => {
            // COMPANY-чат всегда сверху
            if (a.type === 'COMPANY') return -1
            if (b.type === 'COMPANY') return 1
            return 0
        }),
        totalUnread: (state) => state.chats.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    },

    actions: {
        async fetchChats(companyId) {
            this.isLoadingChats = true
            try {
                const response = await api.get(`/companies/${companyId}/chats`)
                console.debug('fetchChats', response.data)
                this.chats = response.data
            } catch (error) {
                console.error('Ошибка загрузки чатов:', error)
            } finally {
                this.isLoadingChats = false
            }
        },

        async fetchMessages(chatId) {
            this.isLoadingMessages = true
            try {
                const response = await api.get(`/chats/${chatId}/messages`)
                console.debug('fetchMessages', response.data)
                this.messages = response.data.content || response.data
                this.hasMoreMessages = !response.data.last
                if (this.messages.length > 0) {
                    this.oldestMessageId = this.messages[this.messages.length - 1].id
                }
            } catch (error) {
                console.error('Ошибка загрузки сообщений:', error)
            } finally {
                this.isLoadingMessages = false
            }
        },

        async fetchContext(chatId, messageId) {
            this.isLoadingMessages = true
            this.isContextMode = true
            this.highlightedMessageId = null
            try {
                const response = await api.get(`/chats/${chatId}/messages/context/${messageId}`, {
                    params: { size: 30 },
                    skipSuccessNotification: true
                })
                console.debug('fetchContext', response.data)
                // бэкенд отдаёт ASC, храним newest-first → реверсируем
                const msgs = [...response.data.messages].reverse()
                this.messages = msgs
                this.hasMoreMessages = response.data.hasBefore
                this.hasMoreMessagesAfter = response.data.hasAfter
                this.oldestMessageId = msgs.length > 0 ? msgs[msgs.length - 1].id : null
                this.newestMessageId = msgs.length > 0 ? msgs[0].id : null
                if (!response.data.hasAfter) {
                    this.isContextMode = false
                }
                this.highlightedMessageId = response.data.targetMessageId
            } catch (error) {
                console.error('Ошибка загрузки контекста:', error)
                this.isContextMode = false
            } finally {
                this.isLoadingMessages = false
            }
        },

        async loadMoreAfter(chatId) {
            if (!this.hasMoreMessagesAfter || this.isLoadingAfter || !this.newestMessageId) return
            this.isLoadingAfter = true
            try {
                const response = await api.get(`/chats/${chatId}/messages`, {
                    params: { after: this.newestMessageId },
                    skipSuccessNotification: true
                })
                console.debug('loadMoreAfter', this.newestMessageId, response.data)
                const newer = response.data.content || response.data
                if (newer.length === 0) {
                    this.hasMoreMessagesAfter = false
                    this.isContextMode = false
                    return
                }
                // newer — ASC, реверсируем в newest-first и вставляем в начало
                const reversed = [...newer].reverse()
                this.messages = [...reversed, ...this.messages]
                this.newestMessageId = reversed[0].id
                if (newer.length < 30) {
                    this.hasMoreMessagesAfter = false
                    this.isContextMode = false
                }
            } catch (error) {
                console.error('Ошибка загрузки новых сообщений:', error)
            } finally {
                this.isLoadingAfter = false
            }
        },

        async loadMoreMessages(chatId) {
            if (!this.hasMoreMessages || this.isLoadingMore || !this.oldestMessageId) return
            this.isLoadingMore = true
            try {
                const response = await api.get(`/chats/${chatId}/messages`, {
                    params: { before: this.oldestMessageId }
                })
                console.debug('loadMoreMessages', this.oldestMessageId, response.data)
                const older = response.data.content || response.data
                this.messages = [...this.messages, ...older]
                if (older.length === 0) {
                    this.hasMoreMessages = false
                    return
                }
                this.hasMoreMessages = !response.data.last
                this.oldestMessageId = older[older.length - 1].id
            } catch (error) {
                console.error('Ошибка загрузки истории:', error)
            } finally {
                this.isLoadingMore = false
            }
        },

        async sendMessage(chatId, content, parentMsgId, fileIds) {
            this.isSending = true
            try {
                const response = await api.post(`/chats/${chatId}/messages`, { content, parentMsgId, fileIds }, {
                    skipSuccessNotification: true
                })
                console.debug('sendMessage', response.data)
                const exists = this.messages.some(m => m.id === response.data.id)
                if (!exists) {
                    this.messages.unshift({ ...response.data, _pending: true })
                }
                playSound('message-sent')
            } catch (error) {
                console.error('Ошибка отправки сообщения:', error)
                throw error
            } finally {
                this.isSending = false
            }
        },

        async deleteMessage(messageId) {
            try {
                await api.delete(`/messages/${messageId}`)
                const msg = this.messages.find(m => m.id === messageId)
                if (msg) msg.deleted = true
            } catch (error) {
                console.error('Ошибка удаления сообщения:', error)
                throw error
            }
        },

        async markAsRead(chatId, lastReadMessageId) {
            const body = { 
                lastReadMessageId: lastReadMessageId 
            }
            try {
                await api.put(`/chats/${chatId}/read`, body, {
                    skipSuccessNotification: true
                })
                this.chats.forEach(chat => {
                    if (chat.id === chatId) {
                        chat.unreadCount = 0
                    }
                })
            } catch (error) {
                console.error('Ошибка обновления курсора:', error)
            }
        },

        async createGroupChat(companyId, name, memberIds) {
            try {
                const response = await api.post(`/companies/${companyId}/chats`, { name, memberIds })
                this.chats.push(response.data)
                return response.data
            } catch (error) {
                console.error('Ошибка создания группового чата:', error)
                throw error
            }
        },

        async fetchMembers(chatId) {
            try {
                const response = await api.get(`/chats/${chatId}/members`)
                console.debug('fetchMembers', response.data)
                this.members = response.data
            } catch (error) {
                console.error('Ошибка загрузки участников:', error)
                throw error
            }
        },

        async addMember(chatId, userId) {
            try {
                await api.post(`/chats/${chatId}/members`, { userId })
            } catch (error) {
                console.error('Ошибка добавления участника:', error)
                throw error
            }
        },

        async removeMember(chatId, userId) {
            try {
                await api.delete(`/chats/${chatId}/members/${userId}`)
            } catch (error) {
                console.error('Ошибка удаления участника:', error)
                throw error
            }
        },

        async searchMessages(chatId, query) {
            if (!query.trim()) {
                this.searchResults = []
                return
            }
            this.isSearching = true
            try {
                const response = await api.get(`/chats/${chatId}/messages/search`, {
                    params: { q: query },
                    skipSuccessNotification: true
                })
                console.debug('searchMessages', response.data)
                this.searchResults = response.data.content || response.data
            } catch (error) {
                console.error('Ошибка поиска:', error)
            } finally {
                this.isSearching = false
            }
        },

        async selectChat(chat) {
            // Отписываемся от предыдущего чата
            if (this.currentSubscription) {
                chatSocket.unsubscribe(this.currentSubscription)
                this.currentSubscription = null
            }

            this.activeChat = chat
            localStorage.setItem('activeChatId', chat.id)
            this.messages = []
            this.hasMoreMessages = true
            this.oldestMessageId = null
            this.newestMessageId = null
            this.hasMoreMessagesAfter = false
            this.isLoadingAfter = false
            this.isContextMode = false
            this.highlightedMessageId = null
            this.searchResults = []

            await this.fetchMessages(chat.id)
            if (this.messages.length > 0) {
                await this.markAsRead(chat.id, this.messages[0].id)
            }

            // Подписываемся на WebSocket-топик чата
            this.currentSubscription = chatSocket.subscribeToChatTopic(chat.id, (chank) => {
                this.onIncomingMessage(chank)
            })
        },

        onIncomingMessage(chank) {
            console.debug('onIncomingMessage', chank)
            const myId = useUserStore().myUserInfo?.id
            const isOwn = chank.message.senderCompanyMember?.userId === myId
            const isOnChatTab = router.currentRoute.value.name === 'chat'

            if (this.activeChat && chank.message.chatId === this.activeChat.id) {
                if (!this.isContextMode) {

                    const pendingIdx = this.messages.findIndex(m => m.id === chank.message.id && m._pending)
                    if (pendingIdx !== -1) {
                        // WS подтвердил — снимаем статус pending
                        this.messages[pendingIdx] = chank.message
                    } else {
                        const exists = this.messages.some(m => m.id === chank.message.id)
                        if (!exists) {
                            console.debug('onIncomingMessage', chank.message)
                            this.messages.unshift(chank.message)
                            if (!isOwn) playSound(isOnChatTab ? 'message-in' : 'notification')

                            if (isOnChatTab) {
                                this.markAsRead(this.activeChat.id, chank.message.id)
                            } else {
                                // Пользователь не видит чат — добавляем к непрочитанным
                                const chat = this.chats.find(c => c.id === chank.message.chatId)
                                if (chat) chat.unreadCount = (chat.unreadCount || 0) + 1
                            }
                        }
                    }
                }
            } else {
                // Инкрементируем счётчик непрочитанных
                const chat = this.chats.find(c => c.id === chank.message.chatId)
                if (chat) chat.unreadCount = (chat.unreadCount || 0) + 1
                if (!isOwn) playSound('notification')

                // if (chank.sourceChatId) {
                //     const sourceChat = this.chats.find(c => c.id === chank.sourceChatId)
                //     if (sourceChat) sourceChat.unreadCount = (sourceChat.unreadCount || 0) + 1
                // }
            }

            // Обновляем превью последнего сообщения в списке чатов
            const chat = this.chats.find(c => c.id === chank.message.chatId)
            if (chat) {
                chat.lastMessage = chank.message
            }
            if (chank.sourceChatId) {
                const sourceChat = this.chats.find(c => c.id === chank.sourceChatId)
                if (sourceChat) {
                    sourceChat.lastMessage = chank.message
                }
            }
        },
    },
})
